import { Empty, Spin } from 'antd';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import IntlMessages from 'util/IntlMessages';

import { COLORS_PALETTE, EXPRESSION_VALUE_TYPE } from '../../../constants';

/**
 * Strict parameters required: 'key' and 'value'.
 * Optional 'label' for display (tooltip, axis) - falls back to key when absent.
 *
 * This component renders a bar chart using Recharts with Ant Design for loading and empty states.
 * It expects the data prop to be an object with a 'metrics' array, where each entry contains
 * at least a 'key' (unique id for chart), 'value' (Y axis), and optionally 'label' (display name).
 */

export const renderBarContent = ({ data, loading, widget }) => {
  const valueType = widget?.queryParams?.valueType;

  if (!data || !data.metrics) {
    return loading ? (
      <Spin size="large" />
    ) : (
      <Empty
        className="gx-m-auto"
        description={<IntlMessages id="components.empty.description" />}
      />
    );
  }

  return (
    <>
      {!loading && !!data.metrics?.length && (
        <ResponsiveContainer width="100%" height="100%" minHeight={0} className="cancelDrag">
          <BarChart data={data.metrics} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
            <XAxis
              dataKey="key"
              angle={-45}
              textAnchor="end"
              height={100}
              interval={0}
              tick={<CustomizedAxisTick data={data.metrics} />}
            />
            <YAxis
              width="auto"
              tickFormatter={
                valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE ? value => `${value}%` : undefined
              }
              domain={valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE ? [0, 100] : ['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip valueType={valueType} />} />
            <Bar dataKey="value">
              <LabelList
                formatter={value => {
                  if (value <= 0) return null;
                  return valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE ? `${value}%` : value;
                }}
                dataKey="value"
                position="top"
              />

              {data.metrics.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS_PALETTE[index % COLORS_PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      {loading && <Spin size="large" />}
      {!loading && !data.metrics?.length && (
        <Empty
          className="gx-m-auto"
          description={<IntlMessages id="components.empty.description" />}
        />
      )}
    </>
  );
};

const CustomizedAxisTick = ({ x, y, payload, data }) => {
  const maxLength = 15;
  const item = data?.find(m => m.key === payload.value);
  const text = (item?.label ?? payload.value) || '';
  const truncatedText = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#666"
        transform="rotate(-45)"
        style={{ fontSize: '12px' }}
      >
        {truncatedText}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, valueType }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const formattedValue =
      valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE ? `${data.value}%` : data.value;

    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 'bold' }}>{data.payload.label ?? data.payload.key}</p>
        <p style={{ margin: '5px 0 0 0', color: data.color }}>{`Value: ${formattedValue}`}</p>
      </div>
    );
  }
  return null;
};
